<template>
    <div class="profile-wrapper">
        <!-- Success Toast -->
        <div v-if="successMessage" class="toast-notification">
            <div class="toast-body">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>{{ successMessage }}</span>
            </div>
        </div>

        <!-- Error Toast -->
        <div v-if="errorMessage" class="toast-notification">
            <div class="toast-body error">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{{ errorMessage }}</span>
                <button class="toast-close-btn" @click="errorMessage = ''">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
            </div>
        </div>

        <div class="profile-header">
            <h1 class="profile-title">Account Settings</h1>
            <p class="profile-subtitle">Manage your profile information and security.</p>
        </div>

        <div class="settings-grid">
            <!-- Profile Details -->
            <div class="settings-card">
                <div class="card-header">
                    <div class="icon-box">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0071C5" stroke-width="2">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                        </svg>
                    </div>
                    <h3>Profile Information</h3>
                </div>

                <form @submit.prevent="updateProfile" class="settings-form">
                    <div class="form-group">
                        <label class="form-label">Full Name</label>
                        <input type="text" v-model="profile.name" class="form-input" required>
                    </div>

                    <div class="form-group">
                        <label class="form-label">Email Address</label>
                        <input type="email" v-model="profile.email" class="form-input" required>
                    </div>

                    <div class="form-group">
                        <label class="form-label">Mobile Number</label>
                        <input type="text" v-model="profile.mobile" class="form-input">
                    </div>

                    <button type="submit" class="btn-save" :disabled="profileLoading">
                        {{ profileLoading ? 'Saving...' : 'Update Profile' }}
                    </button>
                </form>
            </div>

            <!-- Change Password -->
            <div class="settings-card">
                <div class="card-header">
                    <div class="icon-box green">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" stroke-width="2">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                    </div>
                    <h3>Security & Password</h3>
                </div>

                <form @submit.prevent="updatePassword" class="settings-form">
                    <div class="form-group">
                        <label class="form-label">Current Password</label>
                        <input type="password" v-model="passwords.current" class="form-input" required>
                    </div>

                    <div class="form-group">
                        <label class="form-label">New Password</label>
                        <input type="password" v-model="passwords.new" class="form-input" required>
                    </div>

                    <div class="form-group">
                        <label class="form-label">Confirm New Password</label>
                        <input type="password" v-model="passwords.confirm" class="form-input" required>
                    </div>

                    <button type="submit" class="btn-save green" :disabled="passwordLoading">
                        {{ passwordLoading ? 'Updating...' : 'Change Password' }}
                    </button>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'SuperAdminProfile',
    data() {
        return {
            profile: {
                name: '',
                email: '',
                mobile: ''
            },
            passwords: {
                current: '',
                new: '',
                confirm: ''
            },
            profileLoading: false,
            passwordLoading: false,
            successMessage: '',
            errorMessage: ''
        }
    },
    async mounted() {
        await this.fetchProfile();
    },
    methods: {
        showSuccess(message) {
            this.successMessage = message;
            setTimeout(() => { this.successMessage = ''; }, 3000);
        },
        showError(message) {
            this.errorMessage = message;
            setTimeout(() => { this.errorMessage = ''; }, 5000);
        },
        async fetchProfile() {
            try {
                const response = await axios.get(`${this.$API_URL}/super_admin/me`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                this.profile = {
                    name: response.data.name,
                    email: response.data.email,
                    mobile: response.data.mobile
                };
            } catch (err) { console.error(err); }
        },
        async updateProfile() {
            this.profileLoading = true;
            try {
                await axios.put(`${this.$API_URL}/super_admin/profile`, null, {
                    params: {
                        name: this.profile.name,
                        email: this.profile.email,
                        mobile: this.profile.mobile
                    },
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                this.showSuccess('Profile updated successfully');
            } catch (err) {
                this.showError(err.response?.data?.message || err.response?.data?.detail || 'Update failed');
            } finally {
                this.profileLoading = false;
            }
        },
        async updatePassword() {
            if (this.passwords.new !== this.passwords.confirm) {
                this.showError('New passwords do not match');
                return;
            }
            this.passwordLoading = true;
            try {
                await axios.put(`${this.$API_URL}/super_admin/password`, null, {
                    params: {
                        current_password: this.passwords.current,
                        new_password: this.passwords.new
                    },
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                this.showSuccess('Password changed successfully');
                this.passwords = { current: '', new: '', confirm: '' };
            } catch (err) {
                this.showError(err.response?.data?.message || err.response?.data?.detail || 'Password update failed');
            } finally {
                this.passwordLoading = false;
            }
        }
    }
}
</script>

<style scoped>
.profile-wrapper {
    font-family: var(--font-sans);
}

.toast-notification {
    position: fixed;
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 2000;
}

.toast-body {
    background: #10b981;
    color: white;
    padding: 12px 24px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 700;
    box-shadow: 0 10px 25px -5px rgba(16, 185, 129, 0.4);
    animation: toastSlideDown 0.3s ease-out;
}

.toast-body.error {
    background: #ef4444;
    box-shadow: 0 10px 25px -5px rgba(239, 68, 68, 0.4);
}

.toast-close-btn {
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    margin-left: 4px;
    border-radius: 4px;
    color: white;
    opacity: 0.85;
    flex-shrink: 0;
}

.toast-close-btn:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.15);
}

@keyframes toastSlideDown {
    from { transform: translateY(-40px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

.profile-header {
    margin-bottom: 2rem;
}

.profile-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-h1);
    line-height: var(--lh-h1);
    font-weight: var(--fw-h1);
    color: var(--text-primary);
    letter-spacing: -0.02em;
}

.profile-subtitle {
    color: var(--text-secondary);
    margin: var(--space-heading-body) 0 0;
    font-size: var(--fs-sm);
}

.settings-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: 1.5rem;
}

.settings-card {
    background: #fff;
    border: 1px solid #eef2f7;
    border-radius: 20px;
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.04);
    padding: 2rem;
}

.card-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2rem;
}

.icon-box {
    width: 44px;
    height: 44px;
    background: rgba(0, 113, 197, 0.1);
    border-radius: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.icon-box.green { background: rgba(16, 185, 129, 0.1); }

.card-header h3 {
    margin: 0;
    font-size: var(--fs-h3);
    line-height: var(--lh-h3);
    font-weight: var(--fw-h3);
    color: var(--text-primary);
}

.settings-form {
    display: flex;
    flex-direction: column;
    gap: 1.35rem;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.form-label {
    font-size: var(--fs-label);
    font-weight: var(--fw-label);
    letter-spacing: var(--tracking-label);
    text-transform: uppercase;
    color: var(--text-label);
}

.form-input {
    padding: 0.85rem 1rem;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    font-size: 0.95rem;
    font-family: var(--font-sans);
    transition: all 0.2s;
    outline: none;
}

.form-input:focus {
    border-color: #0071C5;
    box-shadow: 0 0 0 4px rgba(0, 113, 197, 0.1);
}

.btn-save {
    margin-top: 0.5rem;
    padding: 0.85rem;
    border: none;
    border-radius: 12px;
    background: linear-gradient(90deg, #0071C5, #0084FF);
    color: white;
    font-weight: 700;
    font-family: var(--font-sans);
    font-size: var(--fs-btn);
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 6px 16px rgba(0, 113, 197, 0.3);
}

.btn-save:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 22px rgba(0, 113, 197, 0.4);
}

.btn-save:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.btn-save.green {
    background: linear-gradient(90deg, #10b981, #059669);
    box-shadow: 0 6px 16px rgba(16, 185, 129, 0.3);
}

.btn-save.green:hover:not(:disabled) {
    box-shadow: 0 10px 22px rgba(16, 185, 129, 0.4);
}
</style>
